import React from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Flags from './flags';
import { getIsRtl } from '../utils';
import { getValue } from './utils';
var useStyles = makeStyles(function (theme) {
  return {
    tableHeadCell: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      backgroundColor: theme.palette.primary.main,
      border: 'inherit',
      borderBottom: 'thin solid ' + theme.palette.grey[500],
      borderRight: 'thin solid ' + theme.palette.grey[500],
      padding: theme.spacing(0.5) + 'px ' + theme.spacing(1) + 'px',
      verticalAlign: 'top',
      '&:focus ': {
        opacity: '.85'
      },
      textAlign: 'center'
    },
    cellContentCenter: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'sticky'
    },
    cellContentRight: {
      display: 'flex',
      justifyContent: 'flex-end',
      fontStyle: 'italic'
    },
    cellLabelHeader: {
      fontStyle: 'inherit',
      fontWeight: 'bold'
    },
    stickyLabel: {
      position: 'sticky'
    }
  };
});
var timesIndexed = R.addIndex(R.times);
var mapIndexed = R.addIndex(R.map);
var Header = function Header(_ref) {
  var headerData = _ref.headerData,
    valuesHeaderData = _ref.valuesHeaderData,
    xLayoutDataLength = _ref.xLayoutDataLength,
    subHeadCellsWidth = _ref.subHeadCellsWidth,
    HTMLRenderer = _ref.HTMLRenderer,
    labelAccessor = _ref.labelAccessor;
  var classes = useStyles();
  var theme = useTheme();
  var isRtl = getIsRtl(theme);
  var stickyLabelPosition = R.last(subHeadCellsWidth) + 30 + theme.spacing(1);
  if (R.compose(R.anyPass([R.isNil, R.isEmpty]), R.path([0, 'data']))(headerData)) return null;
  return timesIndexed(function (index) {
    var flags = R.path([0, 'data', index, 'dimension', 'flags'], headerData);
    var id = 'header_' + index;
    return React.createElement(TableRow, {
      key: id
    }, React.createElement(TableCell, {
      component: 'th',
      padding: 'none',
      align: 'center',
      className: classes.tableHeadCell,
      colSpan: R.inc(xLayoutDataLength),
      id: id,
      tabIndex: 0,
      scope: 'row',
      title: R.pipe(R.path([-1, 'data', index, 'dimension']), labelAccessor)(headerData),
      style: isRtl ? {
        position: 'sticky',
        zIndex: 1,
        right: 0
      } : {
        position: 'sticky',
        zIndex: 1,
        left: 0
      }
    }, React.createElement('div', {
      className: classes.cellContentRight,
      'aria-label': R.path([0, 'data', index, 'dimension', 'id'], headerData),
      'data-testid': 'header-dim',
      role: 'row'
    }, React.createElement(Typography, {
      variant: 'body1',
      className: classes.cellLabelHeader
    }, R.pipe(R.path([-1, 'data', index, 'dimension']), labelAccessor)(headerData), React.createElement(Flags, {
      HTMLRenderer: HTMLRenderer,
      flags: flags,
      isHeader: true,
      labelAccessor: labelAccessor
    })))), mapIndexed(function (headerItem, idx) {
      var _cx;
      var value = headerItem.isEmpty ? {
        label: ''
      } : getValue(labelAccessor)(headerItem);
      var flags = R.propOr([], 'flags', value);
      var hierarchySpace = R.pipe(R.propOr([], 'parents'), R.length, R.times(function () {
        return '·';
      }))(value);
      return React.createElement(TableCell, {
        key: R.prop('id', value) + '-' + idx,
        component: 'th',
        className: cx(classes.tableHeadCell, (_cx = {}, _cx[classes.topCell] = R.not(R.isEmpty(flags)), _cx)),
        id: idx + '_' + index,
        headers: 'header_' + index,
        colSpan: R.prop('spanCount', headerItem),
        tabIndex: 0,
        title: R.prop('label', value),
        'aria-label': R.prop('label', value),
        'aria-describedby': id,
        scope: 'col'
      }, React.createElement('div', {
        className: classes.cellContentCenter
      }, !R.isEmpty(hierarchySpace) && mapIndexed(function (space, idx) {
        return React.createElement('div', {
          key: idx
        }, space);
      })(hierarchySpace), React.createElement(Typography, {
        className: classes.stickyLabel,
        style: {
          left: isRtl ? theme.spacing(1) : stickyLabelPosition,
          right: isRtl ? stickyLabelPosition : theme.spacing(1)
        },
        variant: 'body1'
      }, R.prop('label', value), React.createElement(Flags, {
        HTMLRenderer: HTMLRenderer,
        flags: flags,
        isHeader: true,
        labelAccessor: labelAccessor
      }))));
    }, R.nth(index, valuesHeaderData)));
  }, R.compose(R.length, R.pathOr([], [-1, 'data']))(headerData));
};
Header.propTypes = process.env.NODE_ENV !== "production" ? {
  headerData: PropTypes.array,
  valuesHeaderData: PropTypes.array,
  xLayoutDataLength: PropTypes.number,
  subHeadCellsWidth: PropTypes.array,
  HTMLRenderer: PropTypes.func,
  labelAccessor: PropTypes.func
} : {};
export default Header;